import { useLocale, type Locale } from '@/i18n/locale'
import { Link } from 'react-router-dom'
import { TG_CASES_URL } from '@/lib/contacts'
import './team.css'

type MemberCopy = {
  name: string
  role: string
  body: string
  linkLabel?: string
}

type Member = {
  id: string
  photo: string
  /** Внешняя проверяемая точка (видео-кейсы). */
  link?: string
  /** Страница автора — ru-only, на /en имя остаётся текстом. */
  page?: string
} & Record<Locale, MemberCopy>

const TEAM: Member[] = [
  {
    id: 'anton',
    photo: '/team/anton.jpg',
    link: TG_CASES_URL,
    page: '/author/anton-robaten',
    ru: {
      name: 'Антон Ро Батень',
      role: 'ИИ-архитектор, основатель',
      body: '65+ кейсов масштабирования в 33 нишах и 9 странах — с ростом экономики в цифрах.',
      linkLabel: 'Кейсы в видео →',
    },
    en: {
      name: 'Anton Ro Baten',
      role: 'AI architect, founder',
      body: '65+ business scaling cases across 33 industries and 9 countries — with economic growth shown in numbers.',
      linkLabel: 'Case videos →',
    },
  },
  {
    id: 'andrey',
    photo: '/team/andrey.jpg',
    ru: {
      name: 'Андрей Васильченко',
      role: 'Технический директор (CTO)',
      body: 'Экс-директор по аналитике Авито. МГУ, нейронные сети в астрофизике. 30 кейсов внедрения ИИ-агентов для бизнеса.',
    },
    en: {
      name: 'Andrey Vasilchenko',
      role: 'CTO',
      body: 'Ex-Director of Analytics at Avito. Moscow State University, neural networks in astrophysics. 30 AI-agent implementation cases for business.',
    },
  },
  {
    id: 'gennady',
    photo: '/team/gennady.jpg',
    // Смыслы — с его сайта muhingv.ru (главная, 2026-10-03): 15+ лет в бизнесе,
    // 15+ запущенных стартапов, 25+ проектов в инвестициях, ~1000 разборов
    // предпринимателей. Роль у нас — рост: маркетинг и продажи (Тони, 2026-10-03).
    ru: {
      name: 'Геннадий Мухин',
      role: 'Директор по росту (Head of Growth)',
      body: 'Маркетинг и продажи. Инвестор и предприниматель, 15+ лет в бизнесе: 15+ стартапов, 25+ проектов в инвестициях, около 1000 разборов бизнеса.',
    },
    en: {
      name: 'Gennady Mukhin',
      role: 'Head of Growth',
      body: 'Marketing and sales. Investor and entrepreneur with 15+ years in business: 15+ startups launched, 25+ investments, about 1,000 business reviews with founders.',
    },
  },
]

const COPY = {
  ru: {
    eyebrow: 'Команда · кто ведёт проект',
    heading: ['15 лет систематизации.', 'Год — в ИИ.'],
    lede: 'Проект ведут три человека с разной школой — управленческой, аналитической и предпринимательской. Антон 15 лет систематизирует бизнес: 65 крупных проектов в 33 нишах и 9 странах, в каждом — рост чистой прибыли. Он архитектор, а не исполнитель: определяет, что делать, как, в какой последовательности и зачем, — теперь через ИИ. Андрей руководил аналитикой в Авито, и 30 кейсов внедрения ИИ-агентов — его личный опыт. Геннадий — инвестор и серийный предприниматель: 15+ запущенных стартапов и около 1000 разборов бизнеса; у нас он отвечает за рост — маркетинг и продажи.',
  },
  en: {
    eyebrow: 'Team · who runs your project',
    heading: ['15 years of systems.', 'One in AI.'],
    lede: 'Your project is run by three people from three schools — management, analytics and entrepreneurship. Anton has been systematizing businesses for 15 years: 65 major projects across 33 industries and 9 countries, each with net profit growth. He is the architect, not the contractor: he decides what to do, how, in what order and why — now through AI. Andrey led analytics at Avito, and his 30 AI-agent implementation cases are his personal track record. Gennady is an investor and serial entrepreneur — 15+ startups launched and about 1,000 business reviews with founders; at DNAI he leads growth: marketing and sales.',
  },
}

export function Team() {
  const locale = useLocale()
  const t = COPY[locale]
  return (
    <section id="team" className="section bg-card">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-6 items-end">
          <div className="lg:col-span-6">
            <div className="font-mono text-small uppercase tracking-wider text-muted mb-4">{t.eyebrow}</div>
            {/* Две фразы — две строки: автоперенос рвал вторую посередине. */}
            <h2 className="text-h2 text-ink">
              {t.heading[0]}
              <br />
              {t.heading[1]}
            </h2>
          </div>
          <p className="lg:col-span-6 text-body text-muted max-w-2xl">{t.lede}</p>
        </div>

        {/* Полоса, а не три карточки с квадратным фото во всю ширину: блок
            занимал полтора экрана, а нёс три имени. Волосяная линия сверху
            держит колонки вместе вместо рамок. */}
        <div className="team-strip mt-12 lg:mt-16 grid grid-cols-1 md:grid-cols-3 border-t border-divider">
          {TEAM.map((m) => {
            const c = m[locale]
            return (
              <article key={m.id} className="team-strip__item flex items-start gap-5 py-8 md:pr-8">
                <img
                  src={m.photo}
                  alt={c.name}
                  width={112}
                  height={112}
                  loading="lazy"
                  className="team-strip__photo w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 shrink-0 rounded-card object-cover bg-divider"
                />
                <div className="min-w-0">
                  <h3 className="text-xl text-ink font-medium leading-tight">
                    {m.page && locale === 'ru' ? (
                      <Link to={m.page} className="text-ink no-underline hover:text-accent transition-colors">
                        {c.name}
                      </Link>
                    ) : (
                      c.name
                    )}
                  </h3>
                  <div className="mt-2 font-mono text-small uppercase tracking-wider text-muted">{c.role}</div>
                  <p className="mt-3 text-small text-muted leading-snug">{c.body}</p>
                  {/* Ссылка — только туда, что можно проверить снаружи.
                      Слова о команде без единой проверяемой точки читаются
                      как самоописание; ссылка превращает их в заявление. */}
                  {m.link && c.linkLabel && (
                    <a
                      href={m.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-3 text-small text-accent no-underline hover:underline underline-offset-4"
                    >
                      {c.linkLabel}
                    </a>
                  )}
                </div>
              </article>
            )
          })}
        </div>
      </div>
    </section>
  )
}
